import { supabase } from './supabase';

export type AuditActionType = 'INSERT' | 'UPDATE' | 'DELETE' | 'UPSERT';

export interface AuditLog {
  id?: string;
  userId?: string | null;
  actionType: AuditActionType;
  tableName: string;
  recordId?: string | null;
  oldData?: any | null;
  newData?: any | null;
  createdAt?: string;
}

/**
 * Wrapper pour logger les opérations de base de données dans audit_logs
 */
export const auditLogger = {
  /**
   * Log une action d'audit
   */
  log: async (
    actionType: AuditActionType,
    tableName: string,
    recordId: string | null | undefined,
    oldData: any | null = null,
    newData: any | null = null
  ): Promise<void> => {
    try {
      // Récupérer l'utilisateur actuel
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id || null;

      // Préparer les données d'audit
      const auditLog: Partial<AuditLog> = {
        user_id: userId,
        action_type: actionType,
        table_name: tableName,
        record_id: recordId || null,
        old_data: oldData ? JSON.stringify(oldData) : null,
        new_data: newData ? JSON.stringify(newData) : null
      };

      // Insérer dans audit_logs
      const { error } = await supabase
        .from('audit_logs')
        .insert(auditLog);

      if (error) {
        console.error('Error logging audit:', error);
        // Ne pas throw pour ne pas interrompre les opérations métier
      }
    } catch (error) {
      console.error('Exception in audit logger:', error);
      // Ne pas throw pour ne pas interrompre les opérations métier
    }
  },

  /**
   * Log une opération INSERT
   */
  logInsert: async (
    tableName: string,
    recordId: string | null | undefined,
    newData: any
  ): Promise<void> => {
    await auditLogger.log('INSERT', tableName, recordId, null, newData);
  },

  /**
   * Log une opération UPDATE
   */
  logUpdate: async (
    tableName: string,
    recordId: string | null | undefined,
    oldData: any,
    newData: any
  ): Promise<void> => {
    await auditLogger.log('UPDATE', tableName, recordId, oldData, newData);
  },

  /**
   * Log une opération DELETE
   */
  logDelete: async (
    tableName: string,
    recordId: string | null | undefined,
    oldData: any
  ): Promise<void> => {
    await auditLogger.log('DELETE', tableName, recordId, oldData, null);
  },

  /**
   * Log une opération UPSERT
   */
  logUpsert: async (
    tableName: string,
    recordId: string | null | undefined,
    oldData: any | null,
    newData: any
  ): Promise<void> => {
    await auditLogger.log('UPSERT', tableName, recordId, oldData, newData);
  },

  /**
   * Récupère les logs d'audit pour un enregistrement spécifique
   */
  getLogsForRecord: async (
    tableName: string,
    recordId: string
  ): Promise<AuditLog[]> => {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching audit logs:', error);
        return [];
      }

      return (data || []).map((log: any) => ({
        id: log.id,
        userId: log.user_id,
        actionType: log.action_type as AuditActionType,
        tableName: log.table_name,
        recordId: log.record_id,
        oldData: log.old_data ? JSON.parse(log.old_data) : null,
        newData: log.new_data ? JSON.parse(log.new_data) : null,
        createdAt: log.created_at
      }));
    } catch (error) {
      console.error('Exception fetching audit logs:', error);
      return [];
    }
  },

  /**
   * Récupère les logs d'audit pour un utilisateur spécifique
   */
  getLogsForUser: async (
    userId: string,
    limit: number = 100
  ): Promise<AuditLog[]> => {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching audit logs:', error);
        return [];
      }

      return (data || []).map((log: any) => ({
        id: log.id,
        userId: log.user_id,
        actionType: log.action_type as AuditActionType,
        tableName: log.table_name,
        recordId: log.record_id,
        oldData: log.old_data ? JSON.parse(log.old_data) : null,
        newData: log.new_data ? JSON.parse(log.new_data) : null,
        createdAt: log.created_at
      }));
    } catch (error) {
      console.error('Exception fetching audit logs:', error);
      return [];
    }
  }
};
